import type { ReaderParagraph } from "@/data/curatedChapters";

/** Coerce unknown paragraph rows so pagination never throws on missing fields. */
export function normalizeReaderParagraphsList(input: unknown): ReaderParagraph[] {
  if (!Array.isArray(input)) return [];
  return input.map((raw) => {
    if (!raw || typeof raw !== "object") {
      return { label: "Narrator", text: "" };
    }
    const p = raw as Record<string, unknown>;
    const labelRaw = typeof p.label === "string" ? p.label.trim() : "";
    const text = typeof p.text === "string" ? p.text : "";
    return { label: labelRaw.length > 0 ? labelRaw : "Narrator", text };
  });
}

/** Build page-sized runs of paragraphs for book spreads (character budget per page). */
export function chunkParagraphsIntoPages(paragraphs: ReaderParagraph[] | undefined | null, maxCharsPerPage: number): ReaderParagraph[][] {
  const list = normalizeReaderParagraphsList(paragraphs);
  if (list.length === 0) return [];
  const cap = Number.isFinite(maxCharsPerPage) && maxCharsPerPage > 0 ? maxCharsPerPage : 480;
  const pages: ReaderParagraph[][] = [];
  let current: ReaderParagraph[] = [];
  let running = 0;

  for (const p of list) {
    const blockLen = p.text.length + p.label.length + 8;
    if (running + blockLen > cap && current.length > 0) {
      pages.push(current);
      current = [];
      running = 0;
    }
    current.push(p);
    running += blockLen;
  }
  if (current.length > 0) pages.push(current);
  return pages;
}

export function paragraphsToPlainText(blocks: ReaderParagraph[] | undefined | null): string {
  const list = normalizeReaderParagraphsList(blocks);
  return list.map((b) => (b.label ? `${b.label}: ` : "") + b.text).join("\n\n");
}

/** Map a paragraph index to the spread’s left page index (`flatLeftIndex`) for the current layout. */
export function paragraphIndexToFlatLeftIndex(
  paragraphs: ReaderParagraph[] | undefined | null,
  maxCharsPerPage: number,
  wide: boolean,
  paragraphIndex: number,
): number {
  const list = normalizeReaderParagraphsList(paragraphs);
  const pages = chunkParagraphsIntoPages(list, maxCharsPerPage);
  if (pages.length === 0) return 0;
  const pi = Math.max(0, Math.min(paragraphIndex, list.length - 1));
  let acc = 0;
  for (let i = 0; i < pages.length; i++) {
    const n = pages[i].length;
    if (pi >= acc && pi < acc + n) {
      return wide ? Math.floor(i / 2) * 2 : i;
    }
    acc += n;
  }
  const last = pages.length - 1;
  return wide ? Math.floor(last / 2) * 2 : last;
}
